import type { SystemPackageResourceListResponseApi } from '@ingenico/api-sdk-device-management-private';
import type {
  SystemPackageResourceListResponseV2Api,
  SystemPackageVersionResourceListResponseV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import type { SearchFieldDetailApi } from '@ingenico/api-sdk-transaction-public';
import type { FieldConfiguration } from '@ingenico/react-ui/advanced-search';
import { isEnumValue } from '@ingenico/util-common';
import { toString } from 'lodash-es';
import { versionPublicationStatus } from '../../models/applicationVersion.model';
import type { SelectItems } from '../../models/selectItems.model';
import type { PaginatedSystemPackageListV1, PaginatedSystemPackageListV2 } from '../../models/systemPackage.model';

export function mapSystemPackageListResponseApiV2ToPaginatedSystemPackageList({
  data,
  ...rest
}: SystemPackageResourceListResponseV2Api): PaginatedSystemPackageListV2 {
  return {
    ...rest,
    data: data.map((item) => ({
      id: toString(item.id),
      name: item.name,
      shortDescription: item.shortDescription,
      longDescription: item.longDescription,
      status: isEnumValue(item.status, versionPublicationStatus)
        ? versionPublicationStatus[item.status]
        : versionPublicationStatus.DEPRECATED,
      creationDate: item.creationDate,
      compatibleDeviceFamilyNames: item.compatibleDeviceFamilyNames ?? [],
      systemPackageVersionIds: item.systemPackageVersions.reduce(
        (acc: string[], version) => [
          ...acc,
          ...(version.systemPackageVersionId ? [version.systemPackageVersionId] : []),
        ],
        [],
      ),
      assets: item.assets,
      publisherId: item.publisherId,
    })),
  };
}

export function mapSystemPackageToSelectItems({ data }: SystemPackageResourceListResponseV2Api): SelectItems[] {
  return data.map(({ id, name }) => ({ id: toString(id), label: toString(name) }));
}

export function mapSearchSystemPackageVersionToSelectItems({
  data,
}: SystemPackageVersionResourceListResponseV2Api): SelectItems[] {
  return data.map(({ id, versionNumber }) => ({ id: toString(id), label: toString(versionNumber) }));
}

export function mapSystemPackageListResponseApiV1ToPaginatedSystemPackageList({
  data,
  ...rest
}: SystemPackageResourceListResponseApi): PaginatedSystemPackageListV1 {
  return {
    ...rest,
    data: data.map((item) => ({
      id: item.id ?? '',
      name: item.name,
      logo: item.logo ?? '',
      shortDescription: item.shortDescription,
      longDescription: item.longDescription,
      status: isEnumValue(item.status, versionPublicationStatus)
        ? versionPublicationStatus[item.status]
        : versionPublicationStatus.DEPRECATED,
      creationDate: item.creationDate,
      compatibleDeviceFamilyNames: item.compatibleDeviceFamilyNames ?? [],
      systemPackageVersionIds: item.systemPackageVersionIds ?? [],
    })),
  };
}

export function mapSystemPackageListToFieldConfiguration(response: SearchFieldDetailApi[]): FieldConfiguration[] {
  return response.map(({ key, type }) => ({
    key,
    type,
    visible: true,
    searchable: !key.includes('parent'),
  })) as FieldConfiguration[];
}
